import React, { useEffect, useState } from 'react';
import { formatAgo, formatCountdown, formatUptime, useAdmin } from '../context';
import { AdminPanelDetail, PanelMode, ServerStatus, WeatherView } from '../types';
import { Empty, ViewHeader } from '../ui';

export interface ActivityEntry {
  id: number;
  at: number;
  ok: boolean;
  text: string;
}

interface Props {
  detail: AdminPanelDetail;
  modes: PanelMode[];
  status: ServerStatus | null;
  weather: WeatherView | null;
  activity: ActivityEntry[];
}

const pad = (n: number): string => String(n).padStart(2, '0');

const OverviewViewInner: React.FC<Props> = ({ detail, modes, status, weather, activity }) => {
  const { can, goto, act } = useAdmin();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 5000);
    return () => window.clearInterval(t);
  }, []);

  const online = detail.players.filter((p) => p.online);
  const onlineCount = status ? status.onlinePlayers : online.length;
  const openReports = detail.reports.filter((r) => r.status === 'open').length;
  const modeOn = (id: string): boolean => modes.some((m) => m.id === id && m.active);
  const hasMode = (id: string): boolean => modes.some((m) => m.id === id);

  let clock = '-';
  if (weather && weather.time) clock = `${pad(weather.time.hour)}:${pad(weather.time.minute)}`;

  return (
    <div className="admin-panel__view admin-panel__view--scroll">
      <ViewHeader title="Vue d'ensemble" subtitle="L'état du serveur et vos raccourcis, selon vos permissions." />

      <div className="admin-panel__stat-grid">
        <button type="button" className="admin-panel__stat admin-panel__stat--link" disabled={!can('players')} onClick={() => goto('players')}>
          <span className="admin-panel__status-label">Joueurs en ligne</span>
          <span className="admin-panel__stat-value">{onlineCount}</span>
          <span className="admin-panel__stat-note">{detail.players.length} personnages connus</span>
        </button>
        {can('reports') ? (
          <button type="button" className={'admin-panel__stat admin-panel__stat--link' + (openReports ? ' admin-panel__stat--alert' : '')} onClick={() => goto('reports')}>
            <span className="admin-panel__status-label">Signalements en attente</span>
            <span className="admin-panel__stat-value">{openReports}</span>
            <span className="admin-panel__stat-note">{openReports ? 'À traiter' : 'Rien à traiter'}</span>
          </button>
        ) : null}
        {status && status.restartInSec != null ? (
          <div className="admin-panel__stat admin-panel__stat--alert">
            <span className="admin-panel__status-label">Redémarrage programmé</span>
            <span className="admin-panel__stat-value">{formatCountdown(status.restartInSec)}</span>
            <span className="admin-panel__stat-note">Avant le redémarrage</span>
          </div>
        ) : null}
        {can('weather') ? (
          <button type="button" className="admin-panel__stat admin-panel__stat--link" onClick={() => goto('weather')}>
            <span className="admin-panel__status-label">Météo et heure</span>
            <span className="admin-panel__stat-value admin-panel__stat-value--text">{weather && weather.current ? weather.current.editorId : '-'}</span>
            <span className="admin-panel__stat-note">{weather ? `Heure du jeu ${clock}` : 'Chargement...'}</span>
          </button>
        ) : null}
        <button type="button" className="admin-panel__stat admin-panel__stat--link" onClick={() => goto('status')}>
          <span className="admin-panel__status-label">Serveur</span>
          <span className="admin-panel__stat-value">{status && status.tps != null ? status.tps.toFixed(1) + ' TPS' : '-'}</span>
          <span className="admin-panel__stat-note">{status ? `Actif depuis ${formatUptime(status.uptimeSec)}` : 'Chargement...'}</span>
        </button>
      </div>

      <section className="admin-panel__section" aria-label="Actions rapides">
        <h3 className="admin-panel__group-title">Actions rapides</h3>
        <div className="admin-panel__btnrow">
          {can('broadcast') ? (
            <button type="button" className="admin-panel__button admin-panel__button--primary" onClick={() => goto('broadcast')}>Annonce au serveur</button>
          ) : null}
          {can('teleport') ? (
            <button type="button" className="admin-panel__button" onClick={() => goto('teleport')}>Se téléporter</button>
          ) : null}
          {can('modes') && hasMode('invis') ? (
            <button type="button" aria-pressed={modeOn('invis')} className={'admin-panel__button' + (modeOn('invis') ? ' admin-panel__button--on' : '')} onClick={() => act('toggleMode', { mode: 'invis' }, 'Invisible')}>
              Mode invisible : {modeOn('invis') ? 'actif' : 'inactif'}
            </button>
          ) : null}
          {can('modes') && hasMode('god') ? (
            <button type="button" aria-pressed={modeOn('god')} className={'admin-panel__button' + (modeOn('god') ? ' admin-panel__button--on' : '')} onClick={() => act('toggleMode', { mode: 'god' }, 'Dieu')}>
              Mode dieu : {modeOn('god') ? 'actif' : 'inactif'}
            </button>
          ) : null}
          {can('players') ? (
            <button type="button" className="admin-panel__button" onClick={() => act('reviveZone', { target: '' }, 'Zone de réanimation')}>Réanimer autour de moi</button>
          ) : null}
          {can('reports') && openReports > 0 ? (
            <button type="button" className="admin-panel__button" onClick={() => goto('reports')}>Traiter les signalements</button>
          ) : null}
        </div>
      </section>

      <div className="admin-panel__columns">
        {can('players') ? (
          <section className="admin-panel__section" aria-label="Joueurs en ligne">
            <h3 className="admin-panel__group-title">En ligne maintenant</h3>
            {online.length === 0 ? (
              <Empty title="Personne en ligne" />
            ) : (
              <ul className="admin-panel__mini-list">
                {online.slice(0, 10).map((p) => (
                  <li key={p.p}>
                    <button type="button" className="admin-panel__mini-row" onClick={() => goto('players', { profileId: p.p })}>
                      <span className="admin-panel__dot admin-panel__dot--online" />
                      <span className="admin-panel__cell admin-panel__cell--name">{p.n || '-'}</span>
                      <span className="admin-panel__cell admin-panel__cell--muted">{p.prof || ''}</span>
                      <span className="admin-panel__cell admin-panel__cell--muted admin-panel__num">{p.ping != null ? p.ping + ' ms' : ''}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {online.length > 10 ? (
              <button type="button" className="admin-panel__linkbtn" onClick={() => goto('players')}>Voir les {online.length} joueurs en ligne</button>
            ) : null}
          </section>
        ) : null}

        <section className="admin-panel__section" aria-label="Vos dernières actions">
          <h3 className="admin-panel__group-title">Vos dernières actions</h3>
          {activity.length === 0 ? (
            <Empty title="Aucune action pour l'instant" text="Les réponses du serveur à vos actions s'affichent ici, pendant cette session." />
          ) : (
            <ul className="admin-panel__activity">
              {activity.slice(0, 10).map((a) => (
                <li key={a.id} className={'admin-panel__activity-row' + (a.ok ? '' : ' admin-panel__activity-row--err')}>
                  <span className="admin-panel__activity-text">{a.text}</span>
                  <span className="admin-panel__cell--muted">{formatAgo(a.at, now)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export const OverviewView = React.memo(OverviewViewInner);
